"use client";

import { ProductProvider } from "@/app/directories/shop/[clientSlug]/products/[productId]/components/ProductContext";
import { VerticalHrtSelector } from "@/app/directories/shop/[clientSlug]/products/[productId]/components/VerticalHrtSelector";
import type { StorefrontProductPageHrtType } from "@frontend/components/InsuranceStepper/InsuranceStepper.type";
import {
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import type { GetProductsByCategoryIdQuery } from "@frontend/lib/graphql/generated-gql";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

interface ProductOptionsPaneProps {
  product: GetProductsByCategoryIdQuery["findManyProducts"][number];
  clientSlug: string;
  orgId: number;
  kioskMode: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProductOptionsPane = ({
  product,
  clientSlug,
  orgId,
  kioskMode,
  onOpenChange,
}: ProductOptionsPaneProps) => {
  const queryClient = useQueryClient();
  const configFlags =
    queryClient.getQueryData<Record<string, boolean>>(["configFlags", orgId]) ??
    {};
  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();
  const sendInsurance = useInsuranceConnectorSend();
  const insuranceState = useInsuranceConnectorState();
  const hasAutoOpened = useRef(false);

  // Auto-open the insurance connector when the consumer hasn't been
  // screened yet on orgs that use the eligibility system. The connector
  // handles the DOB/state eligibility flow itself (replacing the old
  // standalone EligibilityModal).
  useEffect(() => {
    if (hasAutoOpened.current) return;
    if (!configFlags?.storefront_offboard_consumers) return;
    if (eligibilityCheckResult.result !== "not_checked") return;
    if (insuranceState.context.uiMode !== "closed") return;
    hasAutoOpened.current = true;
    sendInsurance({ type: "OPEN" });
  }, [
    configFlags?.storefront_offboard_consumers,
    eligibilityCheckResult.result,
    insuranceState.context.uiMode,
    sendInsurance,
  ]);

  // Filter and transform valid HRT options
  const validOptions = product.productToHrt
    .filter((option) => option.hrt != null)
    .map((option) => {
      const hrt = option.hrt as unknown as StorefrontProductPageHrtType;
      return {
        product_component_type: option.product_component_type as
          | "DEFAULT"
          | "INITIAL_REQUIRED",
        hrt: {
          hrtId: hrt.hrtId,
          name: hrt.displayName ?? hrt.name,
          cash_prices: hrt.cash_prices,
          displayMetadata: {
            description: hrt.displayMetadata?.description ?? [],
            ingredients: hrt.displayMetadata?.ingredients ?? [],
          },
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          hrtPaymentType: hrt.hrtPaymentType as any,
          isConsumerFacing: hrt.isConsumerFacing,
          ehrPrimaryAppointmentTypeId:
            hrt.ehrPrimaryAppointmentTypeId as string[],
          isBookableOnStorefront: hrt.isBookableOnStorefront,
          popularity: hrt.popularity,
          HrtTranslation: (hrt.HrtTranslation ?? []) as Array<{
            languageCode: string;
            name?: string;
            displayName?: string;
            displayMetadata?: {
              description?: Array<{ value: string }>;
              ingredients?: Array<{ value: string }>;
            };
          }>,
          // Add productToHrt field required by InsuranceStepper
          productToHrt: [{ productId: product.id }],
        },
      };
    });

  // Don't render if there are no valid HRT options
  if (validOptions.length === 0) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-col gap-2">
          <h2 className="config-h2">{product.name}</h2>
          {product.displayMetadata?.description?.[0]?.value && (
            <p className="config-p3 text-gray-600">
              {product.displayMetadata.description[0].value}
            </p>
          )}
        </div>
        <p className="text-gray-500">No treatment options available.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <ProductProvider initialOptions={validOptions}>
        <VerticalHrtSelector
          clientSlug={clientSlug}
          orgId={orgId}
          product={{
            name: product.name,
            displayMetadata: {
              imageUrl: product.displayMetadata?.imageUrl ?? "",
              preDiscount: product.displayMetadata?.preDiscount ?? "",
            },
            productToHrt: validOptions.map((option) => ({
              hrtId: option.hrt.hrtId,
              product_component_type: option.product_component_type,
              hrt: option.hrt as unknown as StorefrontProductPageHrtType,
            })),
          }}
          onOpenChange={onOpenChange}
          kioskMode={kioskMode}
        />
      </ProductProvider>
    </div>
  );
};
